import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useLocation, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Calculator, CircleCheck, Plus, Save, Trash2, Upload } from "lucide-react";
import { Badge } from "@tubo/ui/components/ui/badge";
import { Button } from "@tubo/ui/components/ui/button";
import { Input } from "@tubo/ui/components/ui/input";
import { Label } from "@tubo/ui/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@tubo/ui/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@tubo/ui/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tubo/ui/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@tubo/ui/components/ui/sheet";
import { Textarea } from "@tubo/ui/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@tubo/ui/components/ui/alert-dialog";
import {
  useSecondaryNavActions,
  useSecondaryNavTitle,
} from "@client/components/secondary-nav";
import {
  useQuantityKindDetail,
  useUpdateUnits,
  useConvertPreview,
  useImportUnits,
  usePublishQuantityKind,
  getQuantityKindDetailQueryKey,
  getQuantityKindListQueryKey,
} from "@tubo/api-models/hooks/quantity-kind-api.hooks";
import type { UnitDefinitionVO, UnitImportResp, UnitImportRowVO } from "@tubo/api-models/hooks/quantity-kind-api.schemas";
import { ConversionRuleFields, ConversionTypeSelect } from "@client/components/semantic-platform/quantities/unit-conversion-fields";
import { QuantityBasicInfoPanel } from "@client/components/semantic-platform/quantities/quantity-basic-info-panel";
import { publishStatusLabel, publishStatusVariant } from "@client/components/semantic-platform/publish-status";

export const Route = createFileRoute("/_app/semantic-platform/quantities/$id")({
  component: QuantityDetailPage,
});

function QuantityDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { searchStr } = useLocation();
  const listSearch = new URLSearchParams(searchStr).get("ns");
  const queryClient = useQueryClient();
  const { data: quantity } = useQuantityKindDetail(id, { query: {} });

  const [units, setUnits] = useState<UnitDefinitionVO[]>([]);
  const [standardUnit, setStandardUnit] = useState("");
  const [calcValue, setCalcValue] = useState("100");
  const [calcUnit, setCalcUnit] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [unitDeleteIdx, setUnitDeleteIdx] = useState<number | null>(null);

  useEffect(() => {
    if (quantity?.units) {
      setUnits(quantity.units);
      setStandardUnit(quantity.standardUnit);
      setCalcUnit((prev) => prev || quantity.standardUnit);
    }
  }, [quantity]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: getQuantityKindDetailQueryKey(id) });
    void queryClient.invalidateQueries({ queryKey: getQuantityKindListQueryKey() });
  };

  const { mutate: saveUnits, isPending: savingUnits } = useUpdateUnits({
    mutation: {
      onSuccess: () => { invalidate(); toast.success("单位与换算规则已保存"); },
      onError: () => toast.error("保存失败(符号重复/标准单位缺失/非标准缺因子/恒等自反)"),
    },
  });

  const publishMutation = usePublishQuantityKind({
    mutation: {
      onSuccess: (detail) => {
        invalidate();
        toast.success(`「${detail?.name ?? "度量"}」已发布（抽取字段绑定与值归一仅消费已发布度量）`);
      },
      onError: () => toast.error("发布失败"),
    },
  });

  const [previewResult, setPreviewResult] = useState<{ standardUnit: string; converted: number; formula: string } | null>(null);
  const calcMutation = useConvertPreview({
    mutation: {
      onSuccess: (res) => setPreviewResult(res),
      onError: () => toast.error("换算失败(单位不存在)"),
    },
  });

  const dirty = quantity
    ? standardUnit !== quantity.standardUnit || JSON.stringify(units) !== JSON.stringify(quantity.units ?? [])
    : false;

  const addUnit = () => setUnits((prev) => [...prev, { symbol: "", name: "" }]);
  const patchUnit = (idx: number, patch: Partial<UnitDefinitionVO>) =>
    setUnits((prev) => prev.map((u, i) => (i === idx ? { ...u, ...patch } : u)));
  const removeUnit = (idx: number) => setUnits((prev) => prev.filter((_, i) => i !== idx));

  // 头部注入域级 SecondaryNav(标题区:返回/编码/名称/标准单位;操作区:换算预览/导入单位/保存),页面体不再自写标题栏
  const headerTitle = useMemo(
    () => (
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon-sm" aria-label="返回列表" onClick={() => navigate({ to: "/semantic-platform/quantities", search: listSearch ? { ns: listSearch } : {} })}>
          <ArrowLeft className="size-4" />
        </Button>
        {quantity ? (
          <>
            <span className="font-mono text-sm text-muted-foreground">{quantity.code}</span>
            <span className="text-sm font-semibold">{quantity.name}</span>
            <Badge variant="outline">标准单位 {quantity.standardUnit}</Badge>
            <Badge variant={publishStatusVariant(quantity.status)}>{publishStatusLabel(quantity.status)}</Badge>
          </>
        ) : (
          <span className="text-sm font-semibold">度量与单位</span>
        )}
      </div>
    ),
    [quantity, navigate, listSearch],
  );
  useSecondaryNavTitle(headerTitle);

  const headerActions = useMemo(
    () =>
      quantity ? (
        <div className="flex items-center gap-2">
          {quantity.status !== "published" && (
            <Button variant="outline" size="sm" disabled={publishMutation.isPending} onClick={() => publishMutation.mutate({ id })}>
              <CircleCheck className="mr-1 size-4" /> 发布
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setPreviewOpen(true)}>
            <Calculator className="mr-1 size-4" /> 换算预览
          </Button>
          <Button variant="outline" size="sm" onClick={() => setImportOpen(true)}>
            <Upload className="mr-1 size-4" /> 导入单位
          </Button>
          <Button size="sm" disabled={!dirty || savingUnits} onClick={() => saveUnits({ id, data: { standardUnit, units } })}>
            <Save className="mr-1 size-4" /> 保存单位配置
          </Button>
        </div>
      ) : null,
    [quantity, dirty, savingUnits, saveUnits, id, standardUnit, units, publishMutation],
  );
  useSecondaryNavActions(headerActions);

  if (!quantity) {
    return <div className="flex h-full items-center justify-center text-muted-foreground">加载中…</div>;
  }

  const unitDeleteTarget = unitDeleteIdx !== null ? units[unitDeleteIdx] : null;

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-auto p-4 xl:p-6">
        <div className="space-y-4">
          <QuantityBasicInfoPanel quantity={quantity} onSaved={invalidate} />

          <section className="rounded-lg border bg-surface/50 p-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold">
              单位与换算(线性/表达式/分段;换算到标准单位)
            </h2>
            <Button variant="outline" size="sm" onClick={addUnit}>
              <Plus className="mr-1 size-4" /> 添加单位
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24 text-left">符号</TableHead>
                <TableHead className="w-32 text-left">名称</TableHead>
                <TableHead className="w-16 text-left">标准?</TableHead>
                <TableHead className="w-28 text-left">换算类型</TableHead>
                <TableHead className="text-left">换算规则</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {units.map((u, idx) => (
                <UnitRow
                  key={idx}
                  unit={u}
                  isStandard={standardUnit === u.symbol}
                  onPatch={(patch) => patchUnit(idx, patch)}
                  onRemove={() => setUnitDeleteIdx(idx)}
                  onMakeStandard={() => {
                    setStandardUnit(u.symbol);
                    setUnits((prev) => prev.map((x, i) => ({ ...x, isStandard: i === idx })));
                  }}
                />
              ))}
            </TableBody>
          </Table>
          </section>
        </div>
      </div>

      <AlertDialog open={unitDeleteIdx !== null} onOpenChange={(open) => !open && setUnitDeleteIdx(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>移除单位「{unitDeleteTarget?.symbol} {unitDeleteTarget?.name}」</AlertDialogTitle>
            <AlertDialogDescription>
              移除在点击「保存单位配置」后生效,保存前刷新页面可放弃更改;被模型属性引用的单位移除后,引用处将无法换算。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => unitDeleteIdx !== null && removeUnit(unitDeleteIdx)}
            >
              确认移除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Sheet open={previewOpen} onOpenChange={setPreviewOpen}>
        <SheetContent className="w-[min(420px,100vw)] gap-0 overflow-y-auto sm:max-w-none">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Calculator className="size-4" />
              换算预览
            </SheetTitle>
            <SheetDescription>
              输入数值与单位，预览换算到标准单位的结果。
            </SheetDescription>
          </SheetHeader>
          <div className="grid gap-4 px-4">
            <div className="grid gap-1.5">
              <Label htmlFor="quantity-preview-value">输入值</Label>
              <Input
                id="quantity-preview-value"
                type="number"
                value={calcValue}
                onChange={(e) => setCalcValue(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="quantity-preview-unit">输入单位</Label>
              <Select
                items={units.filter((u) => u.symbol).map((u) => ({ value: u.symbol, label: u.symbol }))}
                value={calcUnit}
                onValueChange={(v) => setCalcUnit(v ?? "")}
              >
                <SelectTrigger id="quantity-preview-unit" className="w-full">
                  <SelectValue placeholder="选择单位" />
                </SelectTrigger>
                <SelectContent>
                  {units.filter((u) => u.symbol).map((u) => (
                    <SelectItem key={u.symbol} value={u.symbol}>{u.symbol}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="rounded-md bg-muted/40 p-3 text-sm">
              {previewResult ? (
                <>
                  <div className="font-medium">
                    {calcValue} {calcUnit} = <span className="text-primary">{Number(previewResult.converted.toFixed(6))}</span> {previewResult.standardUnit}
                  </div>
                  <div className="mt-1 font-mono text-xs text-muted-foreground">{previewResult.formula}</div>
                </>
              ) : (
                <span className="text-muted-foreground">输入值与单位后点击计算，换算到标准单位</span>
              )}
            </div>
          </div>
          <SheetFooter>
            <Button
              disabled={!calcUnit || calcValue.trim() === "" || isNaN(Number(calcValue)) || calcMutation.isPending}
              onClick={() => calcMutation.mutate({ params: { id }, data: { value: Number(calcValue), fromUnit: calcUnit } })}
            >
              计算
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <ImportUnitsDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        id={id}
        standardUnit={quantity.standardUnit}
        onImported={() => { invalidate(); setImportOpen(false); }}
      />
    </div>
  );
}

/** 单位行(8D-3②:换算类型切换——linear 因子/偏移;expression 受限表达式;piecewise 分段) */
function UnitRow({
  unit,
  isStandard,
  onPatch,
  onRemove,
  onMakeStandard,
}: {
  unit: UnitDefinitionVO;
  isStandard: boolean;
  onPatch: (patch: Partial<UnitDefinitionVO>) => void;
  onRemove: () => void;
  onMakeStandard: () => void;
}) {
  return (
    <TableRow>
      <TableCell><Input className="h-8" value={unit.symbol} onChange={(e) => onPatch({ symbol: e.target.value })} /></TableCell>
      <TableCell><Input className="h-8" value={unit.name} onChange={(e) => onPatch({ name: e.target.value })} /></TableCell>
      <TableCell>
        <input type="radio" name="standard-unit" checked={isStandard} onChange={onMakeStandard} />
      </TableCell>
      <TableCell>
        <ConversionTypeSelect value={unit.conversionType} onChange={(conversionType) => onPatch({ conversionType })} />
      </TableCell>
      <TableCell>
        {isStandard ? (
          <span className="text-xs text-muted-foreground">标准单位(恒等)</span>
        ) : (
          <ConversionRuleFields unit={unit} onPatch={onPatch} />
        )}
      </TableCell>
      <TableCell>
        <Button variant="ghost" size="icon" className="size-7" disabled={isStandard} onClick={onRemove}>
          <Trash2 className="size-3.5" />
        </Button>
      </TableCell>
    </TableRow>
  );
}

/** 外部单位制导入对话框(8D-3③:CSV/UCUM 映射;两段式——预览冲突/影响,确认后执行) */
const IMPORT_MODE_OPTIONS = [
  { value: "merge", label: "冲突跳过(保留现有)" },
  { value: "replace", label: "冲突覆盖" },
];

function ImportUnitsDialog({
  open,
  onOpenChange,
  id,
  standardUnit,
  onImported,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  id: string;
  standardUnit: string;
  onImported: () => void;
}) {
  const [csv, setCsv] = useState("");
  const [mode, setMode] = useState<"merge" | "replace">("merge");
  const [preview, setPreview] = useState<UnitImportResp | null>(null);
  const [draft, setDraft] = useState<UnitImportRowVO[]>([]);

  const importMutation = useImportUnits({
    mutation: {
      onSuccess: (res) => {
        if (res.preview) {
          setPreview(res);
        } else {
          toast.success(`已导入:新增 ${res.addCount},更新 ${res.updateCount}`);
          onImported();
        }
      },
      onError: () => toast.error("导入失败(行格式非法/换算规则校验未过)"),
    },
  });

  /** 解析 CSV:symbol,name,factor,offset[,conversionType,expression];注释行/空行跳过 */
  const parseCsv = () => {
    const rows: UnitImportRowVO[] = [];
    for (const line of csv.split("\n")) {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith("#")) continue;
      const cols = trimmed.split(",").map((s) => s.trim());
      if (cols.length < 2 || !cols[0] || !cols[1]) continue;
      const row: UnitImportRowVO = { symbol: cols[0], name: cols[1] };
      if (cols[2]) row.convertFactor = Number(cols[2]);
      if (cols[3]) row.convertOffset = Number(cols[3]);
      if (cols[4] === "expression") {
        row.conversionType = "expression";
        if (cols[5]) row.expression = cols[5];
      } else if (cols[4] === "piecewise") {
        row.conversionType = "piecewise";
      }
      rows.push(row);
    }
    if (rows.length === 0) {
      toast.error("未解析到有效单位行(symbol,name[,factor,offset[,type,expression]])");
      return;
    }
    setDraft(rows);
    // 首次预览:不执行
    importMutation.mutate({
      id,
      data: { standardUnit, units: rows, mode, confirm: false },
    });
  };

  const confirmImport = () => {
    importMutation.mutate({
      id,
      data: { standardUnit, units: draft, mode, confirm: true },
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { setPreview(null); setDraft([]); } }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>外部单位制导入(CSV/UCUM 映射)</DialogTitle>
          <DialogDescription>
            每行: symbol,name,factor,offset[,conversionType,expression];标准单位固定为 {standardUnit}。
            支持线性(缺省)/表达式(如 x*9/5+32)/分段(piecewise)。先预览冲突与影响,确认后合并。
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <Textarea
            rows={6}
            value={csv}
            placeholder={"# 示例:线性与表达式\nK,开尔文,1,0\nC,摄氏度,1,-273.15\nF,华氏度,1,,expression,x*9/5+32"}
            onChange={(e) => setCsv(e.target.value)}
          />
          <div className="flex items-center gap-2">
            <Select items={IMPORT_MODE_OPTIONS} value={mode} onValueChange={(v) => setMode(v === "replace" ? "replace" : "merge")}>
              <SelectTrigger className="h-8 w-40 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {IMPORT_MODE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={parseCsv} disabled={importMutation.isPending}>
              预览
            </Button>
            {preview && (
              <Button size="sm" onClick={confirmImport} disabled={importMutation.isPending}>
                确认导入
              </Button>
            )}
          </div>
          {preview && (
            <div className="rounded-md border bg-muted/30 p-3 text-sm">
              <p>新增 {preview.addCount} 个 / 更新 {preview.updateCount} 个 / 冲突 {preview.conflictCount} 个;当前被 {preview.modelAttributeCount} 个模型属性引用。</p>
              {preview.conflicts && preview.conflicts.length > 0 && (
                <ul className="mt-1 space-y-0.5 text-xs text-destructive">
                  {preview.conflicts.map((c) => (
                    <li key={c.symbol}>· {c.symbol}(现有:{c.existingName} ← 导入:{c.incomingName})</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>关闭</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
